/**
 * Контракты стартовой диагностики.
 *
 * Диагностика измеряет исходный уровень и выявляет слабые места. Это
 * измерение, а не урок: подсказки минимальны, результат становится baseline
 * (ТЗ §16.3, шаги 5–6).
 */

import type {
  Baseline,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  TypingMetrics,
} from '@type2x/shared-contracts';

/** Задание диагностики. */
export interface DiagnosticTask {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Текст для набора. */
  readonly text: string;
  /** Слова, из которых собран текст. */
  readonly words: readonly string[];
  readonly seed: string;
}

/** Слабое место, выявленное диагностикой. */
export interface WeakSpot {
  readonly unit: MasteryUnit;
  readonly stat: MasteryStat;
  /** Причина, понятная пользователю. */
  readonly reason: 'errors' | 'hesitation' | 'corrections';
  /** Насколько это место проблемное, 0..1. */
  readonly severity: number;
}

/**
 * Ступени уровня набора.
 *
 * Шкала — общепринятая для скорости печати, переведённая из слов в минуту в
 * знаки (одно «слово» теста — пять знаков): до 20, 20–36, 36–56, 56–80 и выше
 * 80 слов в минуту. Границы названы здесь, а не разбросаны по коду: уровень
 * показывается человеку, и менять его молча нельзя.
 */
export const SKILL_LEVELS = ['searching', 'beginner', 'confident', 'fast', 'professional'] as const;
export type SkillLevel = (typeof SKILL_LEVELS)[number];

/** Нижние границы ступеней в знаках в минуту, по возрастанию. */
export const SKILL_LEVEL_FLOOR_CPM: Readonly<Record<SkillLevel, number>> = Object.freeze({
  searching: 0,
  beginner: 100,
  confident: 180,
  fast: 280,
  professional: 400,
});

/** Что делать дальше по итогу диагностики. */
export type RecommendedStart = 'fundamentals' | 'practice';

/**
 * Оценка уровня, которую видит человек.
 *
 * Отдельно от `baseline`: baseline — точка отсчёта для роста, а это — ответ
 * на вопрос «какой у меня уровень», и он считается иначе. Скорость берётся с
 * поправкой на точность: четыреста знаков в минуту при восьмидесяти процентах
 * попаданий — не быстрый набор, а быстрое исправление.
 */
export interface LevelVerdict {
  readonly level: SkillLevel;
  /** Скорость с поправкой на точность, знаков в минуту. */
  readonly effectiveCpm: number;
  /** Медианный промежуток между нажатиями, мс. */
  readonly medianIntervalMs: number;
  /**
   * Попадает почти без промахов, но медленно.
   *
   * Назван по тому, что измерено, а не по тому, что предполагается. Так
   * печатают в двух случаях: когда ищут клавиши глазами и когда движение ещё
   * не стало привычным. Различить их по нажатиям нельзя, и приложение не
   * берётся: глаз человека оно не видит (ТЗ FR-EN-80).
   */
  readonly accurateButSlow: boolean;
  readonly recommendation: RecommendedStart;
}

/** Итог диагностики. */
export interface DiagnosticResult {
  readonly baseline: Baseline;
  readonly metrics: TypingMetrics;
  readonly level: LevelVerdict;
  readonly weakSpots: readonly WeakSpot[];
  readonly units: readonly MasteryUnit[];
  readonly stats: readonly MasteryStat[];
}

/** Минимальное число слабых мест, которое обязана выявить диагностика. */
export const MIN_WEAK_SPOTS = 3;
